import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Card,
  Table,
  Stack,
  Paper,
  Button,
  Popover,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TableHead,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';

import { api } from 'src/api/api-client.config';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useGetContestsQuery } from 'src/api/contests/getContests';
import { useGetCompetitionsQuery } from 'src/api/competitions/getCompetitions';
import { TableHeaderType, ContestUIType } from 'src/types/custom.types';
import { ContestType } from 'src/types/domain.types';
import { TableFilters } from 'src/components/table/Filter';

export default function ContestsPage() {
  const navigate = useNavigate();
  const [open, setOpen] = useState<HTMLElement | null>(null);
  const [openNewContest, setOpenNewContest] = useState(false);
  const [title, setTitle] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const { data: competitions = [], isLoading: isGetCompetitionsLoading } =
    useGetCompetitionsQuery();
  const { data: contests = [], isLoading } = useGetContestsQuery();

  const [filteredData, setFilteredData] = useState<ContestUIType[]>(
    contests as unknown as ContestUIType[]
  );

  const handleOpenMenu = (event: any) => {
    setOpen(event.currentTarget);
    setOpenId(event.currentTarget.id);
  };

  const handleCloseMenu = () => {
    setOpen(null);
    setOpenId(null);
  };

  const handleCreateContest = () => {
    api
      .post(`/contests`, {
        name_en: title,
      })
      .then(() => {
        window.location.reload();
      });
  };

  const duplicatePickemHandler = () => {
    setOpen(null);
    api.post(`/contests/${openId}/duplicate`).then(() => {
      window.location.reload();
    });
  };

  const tableHeader: TableHeaderType[] = [
    { id: 'competition', label: 'Competition' },
    { id: 'name', label: 'Name' },
    { id: 'tenant', label: 'Tenant' },
    { id: 'status', label: 'Status' },
    { id: 'prize_pool', label: 'Prize Pool' },
    { id: 'alive_count', label: 'Alive/Users' },
    { id: 'action', label: 'Action', align: 'right' },
  ];

  const contestsData = useMemo(
    () =>
      contests.map((contest) => ({
        ...contest,
        competition: competitions.find(
          (competition) => competition.competition_id === contest.competition_id
        )?.name_en,
      })),
    [contests, competitions]
  ) as ContestUIType[];

  const handleFilterChange = (filtered: ContestType[]) => {
    setFilteredData(filtered as unknown as ContestUIType[]);
  };

  return (
    <>
      <Helmet>
        <title> Contests </title>
      </Helmet>

      <Container>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          mb={5}
        >
          <Typography variant='h4' gutterBottom>
            Contests
          </Typography>
          <Button
            variant='contained'
            startIcon={<Iconify icon='eva:plus-fill' />}
            onClick={() => setOpenNewContest(true)}
          >
            New Contest
          </Button>
        </Stack>

        {isLoading || isGetCompetitionsLoading ? (
          <Card>
            <Box
              sx={{ display: 'flex', justifyContent: 'center' }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          </Card>
        ) : (
          <>
            <Box>
              {/* Filter Component */}
              <TableFilters
                contestsData={contestsData}
                onFilterChange={handleFilterChange}
              />

              {/* Results Summary */}
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant='body2' color='text.secondary'>
                  Showing {filteredData.length} of {contestsData.length}{' '}
                  contests
                </Typography>
              </Box>
            </Box>

            <Card>
              <Scrollbar>
                <TableContainer component={Paper}>
                  <Table sx={{ minWidth: 650 }} aria-label='contests table'>
                    <TableHead>
                      <TableRow>
                        {tableHeader.map(({ id, label, align = 'left' }) => (
                          <TableCell key={id} align={align}>
                            {label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredData.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={tableHeader.length}
                            align='center'
                          >
                            <Box sx={{ py: 4 }}>
                              <Typography variant='h6' color='text.secondary'>
                                No contests found
                              </Typography>
                              <Typography
                                variant='body2'
                                color='text.secondary'
                              >
                                Try adjusting your filters or search terms
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredData.map((contest) => (
                          <TableRow
                            key={contest.contest_id}
                            sx={{
                              '&:last-child td, &:last-child th': { border: 0 },
                            }}
                          >
                            <TableCell component='th' scope='row'>
                              {contest.competition}
                            </TableCell>
                            <TableCell component='th' scope='row'>
                              {contest.name_en}
                            </TableCell>
                            <TableCell component='th' scope='row'>
                              {contest.tenant}
                            </TableCell>
                            <TableCell component='th' scope='row'>
                              {contest.status === 1 ? 'Active' : 'Draft'}
                            </TableCell>
                            <TableCell component='th' scope='row'>
                              {contest.prize_pool}
                            </TableCell>
                            <TableCell component='th' scope='row'>
                              {contest.alive_count}/{contest.users_count}
                            </TableCell>
                            <TableCell align='right'>
                              <IconButton
                                size='large'
                                color='inherit'
                                onClick={handleOpenMenu}
                                id={contest.contest_id.toString()}
                              >
                                <Iconify icon={'eva:more-vertical-fill'} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>
            </Card>
          </>
        )}
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem
          onClick={() => navigate(`/dashboard/contest?contest_id=${openId}`)}
        >
          <Iconify icon={'eva:edit-outline'} sx={{ mr: 2 }} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() =>
            navigate(`/dashboard/contest-users?contest_id=${openId}`)
          }
        >
          <Iconify icon={'eva:person-fill'} sx={{ mr: 2 }} />
          Users
        </MenuItem>
        <MenuItem onClick={() => duplicatePickemHandler()}>
          <Iconify icon={'eva:copy-outline'} sx={{ mr: 1 }} />
          Duplicate
        </MenuItem>
      </Popover>
      <Dialog
        open={openNewContest}
        onClose={() => {}}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>Create Contest</DialogTitle>
        <DialogContent>
          <TextField
            id='name_en'
            label='Title'
            fullWidth
            value={title}
            variant='standard'
            onChange={(e) => setTitle(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewContest(false)}>Cancel</Button>
          <Button onClick={() => handleCreateContest()} autoFocus>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
